/*
 * The Cabinet Shop primitives: behaviour.
 *
 * Small on purpose: native <dialog> gives focus trapping, Esc and an inert
 * background for free, so this file only adds what the platform doesn't:
 * the closing animation, pull-down-to-dismiss on a phone sheet, toasts,
 * button states and the action bar.
 *
 *   <button data-tcs-open="sheet-id">       opens <dialog class="tcs-overlay" id="sheet-id">
 *   <a href="…" data-tcs-open="sheet-id">   the same, and still a plain link where that dialog isn't on the page
 *   <button data-tcs-close>                 closes the dialog it sits in
 *   tcs.toast({ title, description, tone }) a quiet confirmation
 *   tcs.buttonState(button, "working" | "done" | "idle")
 *   <div data-tcs-action-bar="#source">     shows while #source is out of view
 */
(() => {
  if (window.tcs) return;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Waits for the element's exit animation, if it has one, then runs `done`.
  const afterExit = (element, done) => {
    const animated = !reduceMotion() && getComputedStyle(element).animationName !== 'none';
    if (!animated) return done();
    element.addEventListener('animationend', done, { once: true });
  };

  /* ---- Overlay ------------------------------------------------------- */

  const open = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog.returnFocusTo = opener || document.activeElement;
    dialog.removeAttribute('data-closing');
    dialog.showModal();
    dialog.dispatchEvent(new CustomEvent('tcs:open'));
  };

  const close = (dialog) => {
    if (!dialog || !dialog.open || dialog.hasAttribute('data-closing')) return;
    dialog.setAttribute('data-closing', '');
    afterExit(dialog, () => {
      dialog.close();
      dialog.removeAttribute('data-closing');
      dialog.style.translate = '';
      dialog.returnFocusTo?.focus?.({ preventScroll: true });
      dialog.dispatchEvent(new CustomEvent('tcs:close'));
    });
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-tcs-open]');
    const target = opener && document.getElementById(opener.dataset.tcsOpen);
    if (target) {
      event.preventDefault();
      // a sheet opened from inside another (the menu's "Start designing") replaces it
      const host = opener.closest('dialog.tcs-overlay[open]');
      if (host && host !== target) close(host);
      open(target, host ? null : opener);
      return;
    }
    // no such dialog on this page: a link opener just goes where it points
    const closer = event.target.closest('[data-tcs-close]');
    if (closer) {
      close(closer.closest('dialog'));
      return;
    }
    // A press on the backdrop (the dialog element itself, outside its content) closes it.
    const dialog = event.target.closest('dialog.tcs-overlay');
    if (dialog && event.target === dialog) {
      const box = dialog.getBoundingClientRect();
      const inside =
        event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
      if (!inside) close(dialog);
    }
  });

  // Esc animates out too, instead of vanishing.
  document.addEventListener(
    'cancel',
    (event) => {
      if (event.target.matches?.('dialog.tcs-overlay')) {
        event.preventDefault();
        close(event.target);
      }
    },
    true,
  );

  // Phone sheets follow the finger down from their handle or header, and close past a threshold.
  let drag = null;
  document.addEventListener(
    'pointerdown',
    (event) => {
      const grip = event.target.closest('.tcs-overlay__handle, .tcs-overlay__header');
      const dialog = grip?.closest('dialog.tcs-overlay[open]');
      if (!dialog || window.innerWidth >= 768 || event.target.closest('button, a, input')) return;
      drag = { dialog, startY: event.clientY, startTime: event.timeStamp, dy: 0 };
      dialog.setAttribute('data-dragging', '');
    },
    { passive: true },
  );
  document.addEventListener(
    'pointermove',
    (event) => {
      if (!drag) return;
      drag.dy = Math.max(0, event.clientY - drag.startY);
      drag.dialog.style.translate = `0 ${drag.dy}px`;
    },
    { passive: true },
  );
  const endDrag = (event) => {
    if (!drag) return;
    const { dialog, dy, startTime } = drag;
    drag = null;
    dialog.removeAttribute('data-dragging');
    const fast = dy / Math.max(1, event.timeStamp - startTime) > 0.5;
    if (dy > dialog.offsetHeight * 0.3 || (fast && dy > 24)) {
      close(dialog);
    } else {
      dialog.animate([{ translate: `0 ${dy}px` }, { translate: '0 0' }], {
        duration: reduceMotion() ? 0 : 200,
        easing: 'cubic-bezier(0.32, 0.72, 0, 1)',
      });
      dialog.style.translate = '';
    }
  };
  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  /* ---- Toast --------------------------------------------------------- */

  const icon = (name) =>
    name === 'check'
      ? '<svg class="tcs-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>'
      : '<svg class="tcs-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>';

  let toaster;
  const toast = ({ title, description = '', tone = 'success', action, duration = 4000 } = {}) => {
    if (!toaster) {
      toaster = document.createElement('div');
      toaster.className = 'tcs-toaster';
      toaster.setAttribute('role', 'status');
      toaster.setAttribute('aria-live', 'polite');
      document.body.append(toaster);
    }
    const item = document.createElement('div');
    item.className = 'tcs-toast tcs-material';
    item.dataset.tone = tone;
    item.innerHTML = `${icon(tone === 'danger' ? 'alert' : 'check')}<div class="tcs-toast__text"><p class="tcs-toast__title"></p>${
      description ? '<p class="tcs-toast__description"></p>' : ''
    }</div>`;
    item.querySelector('.tcs-toast__title').textContent = title;
    if (description) item.querySelector('.tcs-toast__description').textContent = description;
    if (action) {
      const link = document.createElement(action.href ? 'a' : 'button');
      link.className = 'tcs-button tcs-button--ghost tcs-button--compact';
      link.textContent = action.label;
      if (action.href) link.href = action.href;
      else link.type = 'button';
      if (action.onClick) link.addEventListener('click', action.onClick);
      item.append(link);
    }
    toaster.append(item);
    const dismiss = () => {
      if (item.hasAttribute('data-closing')) return;
      item.setAttribute('data-closing', '');
      afterExit(item, () => item.remove());
    };
    // Pausing while hovered or focused gives people time to read and to reach the action.
    let timer = setTimeout(dismiss, duration);
    const pause = () => clearTimeout(timer);
    const resume = () => (timer = setTimeout(dismiss, duration / 2));
    item.addEventListener('pointerenter', pause);
    item.addEventListener('pointerleave', resume);
    item.addEventListener('focusin', pause);
    item.addEventListener('focusout', resume);
    return dismiss;
  };

  /* ---- Button state -------------------------------------------------- */

  // Adds the working and done layers once, so any .tcs-button can show them.
  const buttonState = (button, state) => {
    if (!button) return;
    if (!button.querySelector('.tcs-button__label')) {
      const label = document.createElement('span');
      label.className = 'tcs-button__label';
      label.append(...button.childNodes);
      button.append(label);
    }
    if (!button.querySelector('.tcs-button__state')) {
      button.insertAdjacentHTML(
        'beforeend',
        '<span class="tcs-button__state tcs-button__state--working" aria-hidden="true"><svg class="tcs-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg></span>' +
          '<span class="tcs-button__state tcs-button__state--done" aria-hidden="true"><svg class="tcs-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span>',
      );
    }
    if (state === 'idle') {
      button.removeAttribute('data-state');
      button.removeAttribute('aria-busy');
    } else {
      button.dataset.state = state;
      button.setAttribute('aria-busy', String(state === 'working'));
    }
  };

  /* ---- Action bar ---------------------------------------------------- */

  const setupActionBars = (root = document) => {
    root.querySelectorAll('[data-tcs-action-bar]').forEach((bar) => {
      if (bar.tcsObserved) return;
      const source = document.querySelector(bar.dataset.tcsActionBar);
      if (!source || !('IntersectionObserver' in window)) return;
      bar.tcsObserved = true;
      // A floating pill sits clear of the chat launcher, so it doesn't push anything up.
      const floats = bar.classList.contains('tcs-action-bar--float');
      const until = bar.dataset.tcsActionBarUntil ? document.querySelector(bar.dataset.tcsActionBarUntil) : null;
      let sourceGone = false;
      let untilShown = false;
      const update = () => {
        const show = sourceGone && !untilShown;
        bar.toggleAttribute('data-visible', show);
        bar.inert = !show;
        if (!floats) document.documentElement.style.setProperty('--action-bar-height', show ? `${bar.offsetHeight}px` : '0px');
      };
      new IntersectionObserver(([entry]) => {
        // Only once the source has scrolled away above: never while it's still below, unseen.
        sourceGone = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        update();
      }).observe(source);
      // And out of the way again once the page's own closing action is on screen.
      if (until) {
        new IntersectionObserver(([entry]) => {
          untilShown = entry.isIntersecting || entry.boundingClientRect.top < 0;
          update();
        }).observe(until);
      }
    });
  };

  // Tidio's launcher moves above the action bar (see tcs-components.css), except while its chat is open.
  document.addEventListener('tidioChat-ready', () => {
    const api = window.tidioChatApi;
    if (!api) return;
    api.on('open', () => document.documentElement.classList.add('tcs-chat-open'));
    api.on('close', () => document.documentElement.classList.remove('tcs-chat-open'));
  });

  // Product pages: the pinned price sits exactly one Add-to-cart height above the pinned Add to cart
  // (see tcs-sections.css). Measured now, on load, whenever the block resizes, and again if the
  // configurator or a variant change re-renders it, so the two never drift apart.
  const measureBuy = () => {
    const info = document.querySelector('.wt-product__info');
    if (!info) return;
    let observed = null;
    const resize = 'ResizeObserver' in window ? new ResizeObserver(() => set()) : null;
    const set = () => {
      const buy = info.querySelector(':scope > .main-product__buy-buttons--container');
      if (!buy) return;
      document.documentElement.style.setProperty('--tcs-buy-h', `${Math.round(buy.getBoundingClientRect().height)}px`);
      if (resize && buy !== observed) {
        resize.disconnect();
        resize.observe(buy);
        observed = buy;
      }
    };
    set();
    new MutationObserver(set).observe(info, { childList: true });
    window.addEventListener('load', set, { once: true });
  };

  // Two apps draw inside their own shadow roots: the cookie banner (Consentmo) and the chat (Tidio, loaded
  // on first use). Each gets the design language's skin as a stylesheet inside that root
  // (assets/tcs-consent.css, assets/tcs-chat.css). Their wording and behaviour stay the apps'.
  const skinShadow = (selector, href) => {
    const host = document.querySelector(selector);
    const shadow = host?.shadowRoot;
    if (!shadow || !href) return false;
    if (shadow.tcsSkinned) return true;
    shadow.tcsSkinned = true;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-tcs-skin', '');
    shadow.append(link);
    // The apps add their own stylesheets after first paint; the skin stays last so it has the final say.
    new MutationObserver(() => {
      if (shadow.lastElementChild !== link) shadow.append(link);
    }).observe(shadow, { childList: true });
    return true;
  };
  const skins = [
    ['csm-cookie-consent', window.tcsConsentCss, 20000],
    ['#tidio-chat', window.tcsChatCss, 0],
  ];
  for (const [selector, href, giveUpAfter] of skins) {
    if (!href || skinShadow(selector, href)) continue;
    const watcher = new MutationObserver(() => skinShadow(selector, href) && watcher.disconnect());
    watcher.observe(document.documentElement, { childList: true, subtree: true });
    // The banner comes early or not at all; the chat arrives whenever someone first opens it.
    if (giveUpAfter) setTimeout(() => watcher.disconnect(), giveUpAfter);
  }
  // Tidio may attach its shadow root a moment after adding its element; its ready event is the backstop.
  document.addEventListener('tidioChat-ready', () => skinShadow('#tidio-chat', window.tcsChatCss));

  // Pictures fade up from their stage as they arrive, rather than appearing in one frame. Only those not
  // yet loaded when found (so nothing already on screen blinks), including ones added later (filters,
  // search, panels). Without JavaScript, pictures simply show.
  const FADE = '.tcs-card__image:not(.tcs-card__image--second), .tcs-category__image img, .tcs-tile__media img, .tcs-nav-tile__image img, .tcs-search__card-image img, .tcs-story-tile__image, .tcs-step__media img, .tcs-finishes__stage img';
  const prime = (scope) => {
    scope.querySelectorAll?.(FADE).forEach((img) => {
      if (img.hasAttribute('data-tcs-fade') || (img.complete && img.naturalWidth)) return;
      img.setAttribute('data-tcs-fade', '');
      const done = () => img.setAttribute('data-tcs-loaded', '');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  };
  new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach((node) => node.nodeType === 1 && prime(node)))).observe(document.documentElement, { childList: true, subtree: true });

  // A row that scrolls sideways (rails swipe on phones) must be reachable by keyboard: when it holds nothing
  // focusable and it actually overflows, it takes focus itself (arrow keys then scroll it) and is named by
  // its section's heading. On wider screens, where it is a plain grid, it isn't a tab stop.
  const setupRails = () => {
    const rails = [...document.querySelectorAll('.tcs-rail, .tcs-steps')];
    if (!rails.length) return;
    const update = () => rails.forEach((rail) => {
      const scrolls = rail.scrollWidth > rail.clientWidth + 1;
      const hasFocusable = !!rail.querySelector('a[href], button, input, select, textarea, [tabindex]');
      if (scrolls && !hasFocusable) {
        rail.tabIndex = 0;
        if (!rail.hasAttribute('aria-label')) {
          const heading = rail.closest('section, .tcs-region')?.querySelector('h2, h3');
          rail.setAttribute('aria-label', heading ? heading.textContent.trim() : 'Scrollable list');
          rail.dataset.tcsRailLabel = '';
        }
      } else if (rail.tabIndex === 0) {
        rail.removeAttribute('tabindex');
        if (rail.hasAttribute('data-tcs-rail-label')) { rail.removeAttribute('aria-label'); rail.removeAttribute('data-tcs-rail-label'); }
      }
    });
    update();
    let frame;
    window.addEventListener('resize', () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(update); });
  };

  // Product pages with the configurator: until it has drawn and priced the design, the theme's price and
  // Add to cart still describe the base product (and the configurator itself prices its default before the
  // product's saved design), so both are held back until the price has been steady for half a second. If
  // the designer hasn't arrived after 12 seconds, the customer is told and offered a retry or our help;
  // Add to cart stays off, because nothing on the page describes their design. The configurator itself is
  // untouched: this only reads its placeholders ([data-configurator-skeleton]) and the price it writes.
  const setupConfiguratorGuard = () => {
    if (!document.querySelector('[data-configurator-block-type="controls"]')) return;
    const card = document.querySelector('.tcs-price-card');
    const form = document.querySelector('form[action*="/cart/add"] [name="add"]')?.closest('form');
    const buttons = () => [...document.querySelectorAll('form[action*="/cart/add"] [name="add"], form[action*="/cart/add"] .product-form__submit')];
    if (!card && !form) return;
    const root = document.documentElement;
    root.setAttribute('data-tcs-pricing', 'pending');
    const held = new Set();
    const hold = () => buttons().forEach((button) => { if (!button.disabled) { button.disabled = true; held.add(button); } button.setAttribute('aria-busy', 'true'); });
    const release = () => { held.forEach((button) => (button.disabled = false)); held.clear(); buttons().forEach((button) => button.removeAttribute('aria-busy')); };
    hold();
    let settle;
    let done = false;
    const ready = () => !document.querySelector('[data-configurator-skeleton]');
    const finish = () => {
      if (done) return;
      done = true;
      observer.disconnect();
      blocks.disconnect();
      clearTimeout(failTimer);
      root.setAttribute('data-tcs-pricing', 'ready');
      release();
    };
    const check = () => {
      if (done) return;
      clearTimeout(settle);
      if (ready()) settle = setTimeout(finish, 500);
    };
    // The price and the button are what must be steady; the configurator's own blocks are only watched for
    // their placeholders going (the 3D viewer animates for seconds and mustn't hold the price back).
    const observer = new MutationObserver(check);
    [card, form].filter(Boolean).forEach((node) => observer.observe(node, { childList: true, subtree: true, characterData: true }));
    let wasReady = ready();
    const blocks = new MutationObserver(() => { if (!wasReady && ready()) { wasReady = true; check(); } });
    document.querySelectorAll('[data-configurator-block]').forEach((node) => blocks.observe(node, { childList: true, subtree: true }));
    check();
    const failTimer = setTimeout(() => {
      if (done || ready()) return;
      root.setAttribute('data-tcs-pricing', 'failed');
      const contact = window.Shopify?.routes?.root ? `${window.Shopify.routes.root}pages/contact` : '/pages/contact';
      const notice = document.createElement('div');
      notice.className = 'tcs-notice tcs-notice--danger tcs-configurator-failed';
      notice.setAttribute('role', 'alert');
      notice.innerHTML = '<div><p><strong>The designer hasn’t loaded</strong>, so we can’t show your price yet.</p><p class="tcs-configurator-failed__actions"><button type="button" class="tcs-button tcs-button--secondary tcs-button--compact" data-tcs-retry>Try again</button> <a class="tcs-link" href="' + contact + '">Ask our design team</a></p></div>';
      notice.querySelector('[data-tcs-retry]').addEventListener('click', () => window.location.reload());
      (card || form).insertAdjacentElement('beforebegin', notice);
    }, 12000);
  };

  document.addEventListener('DOMContentLoaded', () => {
    setupConfiguratorGuard();
    setupRails();
    setupActionBars();
    measureBuy();
    skinShadow('csm-cookie-consent', window.tcsConsentCss);
    prime(document);
  });
  document.addEventListener('shopify:section:load', (event) => setupActionBars(event.target));

  /* ---- Moving between pages ---- */

  // The card you open lends its name to the product page's title (see tcs-components.css).
  // Named only on the click, and cleared when the page is shown again, so two elements never share it.
  // The picture you choose becomes the product page's picture: the card's (or tile's) image is named
  // on the click, the product gallery carries the same name, and the browser moves one into the other.
  // A name may be used once per page, so the page's own product names step aside first.
  document.addEventListener('click', (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    const link = event.target.closest('a[href*="/products/"]');
    if (!link) return;
    const media = link.querySelector('[data-tcs-morph]') || (link.matches('[data-tcs-morph]') ? link : null);
    const title = link.querySelector('.tcs-card__title');
    if (!media && !title) return;
    document.querySelectorAll('.wt-product__gallery, .wt-product__name').forEach((node) => (node.style.viewTransitionName = 'none'));
    if (media) media.style.viewTransitionName = 'tcs-product-media';
    if (title) title.style.viewTransitionName = 'tcs-product-title';
  });

  // Direction (forward or back) is set in the layout's head: pagereveal fires before deferred scripts run.
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-tcs-morph][style*="view-transition-name"]').forEach((node) => (node.style.viewTransitionName = ''));
    document.querySelectorAll('.wt-product__gallery[style*="view-transition-name"], .wt-product__name[style*="view-transition-name"]').forEach((node) => (node.style.viewTransitionName = ''));
    document.querySelectorAll('.tcs-card__title[style*="view-transition-name"]').forEach((title) => {
      title.style.viewTransitionName = '';
    });
  });

  /* ---- Reveal ---------------------------------------------------------- */

  // A section's head and its first row rise into place once, as the section comes into view: the head
  // first, then the items one after another, at the "space" speed. Only sections that start below the
  // first screen take part, so nothing on screen at arrival is ever hidden, and with reduced motion (or
  // without this script) everything is simply there. The hero and the films have motion of their own.
  const REVEAL_ITEMS = '.tcs-stats__item, .tcs-shelf__item, .tcs-rail > li, .tcs-steps > li, .tcs-faq__item, .tcs-logos > li';
  const revealSetup = () => {
    if (reduceMotion() || !('IntersectionObserver' in window)) return;
    const regions = [...document.querySelectorAll('main .tcs-region')].filter(
      (region) => !region.closest('.tcs-hero, [data-tcs-closing]') && region.getBoundingClientRect().top > window.innerHeight,
    );
    if (!regions.length) return;
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        entry.target.setAttribute('data-revealed', '');
      }
    }, { rootMargin: '0px 0px -12% 0px' });
    for (const region of regions) {
      const parts = [region.querySelector('.tcs-section-head'), ...[...region.querySelectorAll(REVEAL_ITEMS)].slice(0, 6)].filter(Boolean);
      if (!parts.length) continue;
      parts.forEach((part, i) => {
        part.classList.add('tcs-reveal');
        part.style.setProperty('--reveal-delay', `${i * 60}ms`);
      });
      region.setAttribute('data-reveal', '');
      observer.observe(region);
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', revealSetup);
  else revealSetup();

  /* ---- Questions: answers that open and close smoothly --------------------- */

  // A question's answer grows open on the spring and its words rise in just behind it; closing folds it
  // away faster. The plus turns into a cross as the click lands, not when the motion ends. A click mid-way
  // reverses from where it is. With reduced motion (or without this script) the answer simply appears.
  const accordion = (item) => {
    const summary = item.querySelector(':scope > summary');
    const answer = item.querySelector(':scope > .tcs-faq__answer');
    if (!summary || !answer || item.tcsAccordion) return;
    item.tcsAccordion = true;
    let motion = null;
    const SPRING = 'cubic-bezier(0.22, 1, 0.36, 1)';
    const finish = (open) => {
      motion = null;
      item.open = open;
      item.classList.remove('is-opening', 'is-closing');
      answer.style.overflow = answer.style.height = answer.style.boxSizing = '';
    };
    summary.addEventListener('click', (event) => {
      if (reduceMotion() || !answer.animate) return;
      event.preventDefault();
      const opening = !item.open || item.classList.contains('is-closing');
      // A closed answer still has a size in newer browsers (its slot is only hidden), so closed counts as 0.
      const from = item.open ? answer.getBoundingClientRect().height : 0;
      if (motion) motion.cancel();
      item.classList.toggle('is-opening', opening);
      item.classList.toggle('is-closing', !opening);
      answer.style.overflow = 'hidden';
      // Heights are measured outside the padding, so they are set the same way.
      answer.style.boxSizing = 'border-box';
      if (opening) {
        item.open = true;
        answer.style.height = '';
        const to = answer.getBoundingClientRect().height;
        const pad = getComputedStyle(answer).paddingBottom;
        motion = answer.animate(
          { height: [`${from}px`, `${to}px`], paddingBottom: [from ? pad : '0px', pad] },
          { duration: 500, easing: SPRING },
        );
        [...answer.children].forEach((part, i) =>
          part.animate(
            { opacity: [0, 1], transform: ['translateY(6px)', 'none'] },
            { duration: 420, delay: 60 + i * 40, easing: SPRING, fill: 'backwards' },
          ),
        );
      } else {
        const pad = getComputedStyle(answer).paddingBottom;
        motion = answer.animate(
          { height: [`${from}px`, '0px'], paddingBottom: [pad, '0px'], opacity: [1, 0.2] },
          { duration: 250, easing: 'ease-in-out' },
        );
      }
      const current = motion;
      motion.onfinish = () => current === motion && finish(opening);
    });
  };
  const accordionSetup = () => document.querySelectorAll('details.tcs-faq__item').forEach(accordion);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', accordionSetup);
  else accordionSetup();
  document.addEventListener('shopify:section:load', accordionSetup);

  /* ---- Count-up ------------------------------------------------------ */

  // A stat figure that starts with a number of 10 or more ("1,200 cabinets", "15 years") counts up
  // from zero once, the first time it comes into view, on the spring's curve. Small numbers don't:
  // "1 weeks, 2 weeks" reads as a glitch. With reduced motion it is simply there.
  const countSetup = () => {
    if (reduceMotion() || !('IntersectionObserver' in window)) return;
    const figures = [...document.querySelectorAll('[data-tcs-count]')].filter((el) => {
      const match = el.textContent.trim().match(/^[\d.,]+/);
      return match && parseFloat(match[0].replace(/,/g, '')) >= 10;
    });
    if (!figures.length) return;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const run = (el) => {
      const text = el.textContent.trim();
      const [, digits, rest] = text.match(/^([\d.,]+)(.*)$/);
      const target = parseFloat(digits.replace(/,/g, ''));
      const decimals = (digits.split('.')[1] || '').length;
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / 600);
        el.textContent = (target * ease(t)).toFixed(decimals) + rest;
        if (t < 1) requestAnimationFrame(tick);
        else el.textContent = text;
      };
      requestAnimationFrame(tick);
    };
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        run(entry.target);
      }
    }, { rootMargin: '0px 0px -10% 0px' });
    figures.forEach((el) => observer.observe(el));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', countSetup);
  else countSetup();

  window.tcs = { open: (id, opener) => open(document.getElementById(id), opener), close, toast, buttonState };
})();
